import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Flex from 'antd/lib/flex';
import Spin from 'antd/lib/spin';
import Popover from 'antd/lib/popover';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { compact, flatMap, groupBy, isNil, keyBy, maxBy, mean, minBy, sortBy, uniq } from 'lodash';
import { RootState } from '@/reducers/reducers';
import {
    selectIsAppsDemoModeEnabled,
    selectMonitoringMetricsById,
    selectStabilityIntegrations,
    selectStepConfigsByType,
    selectStepRunsFromSummary,
} from '@/selectors/selectors';
import { useGetRolloutGroupSummaryQuery } from '@/middlewares/defaultApi';
import { buildDemoRolloutGroupSummary } from '@/helpers/demoHelpers';
import {
    AggregatedMonitoringStatistic,
    AllStabilityMetricTypes,
    AllUserRatingsMetricType,
    DestinationMonitoringStatistic,
    DestinationAdoption,
    DestinationPhasedRelease,
    IntegrationType,
    MonitoringMetricHealthStatus,
    MonitoringStatistic,
    ReleaseStepType,
    RolloutStepRunData,
    StabilityInfo,
    StabilityIssue,
    StabilityMetricType,
    UserRatingsMetricType,
    VersionAdoption,
} from '@/types';
import { Destination } from '@/types/destinations';
import { GroupedMonitoringStatistic, MonitoringStatisticGroup } from '@/typescriptHelpers';
import {
    displayMonitoringStatisticFormattedDelta,
    displayMonitoringStatisticFormattedValue,
    getIntegrationTypeForAppStoreType,
    massageMonitoringData,
} from '@/helpers';
import { colorForMonitoringMetricHealthStatus, displayStringForDestination } from '@/helpers/displayHelpers';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { antGrey, runwayColors, tagStyle } from '@/components/styles/globalStyles';
import Tag from 'antd/lib/tag';
import { AppstoreOutlined, ThunderboltFilled } from '@ant-design/icons';
import { PhasedReleaseChartBase } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import {
    MonitoringMetricCard,
    monitoringMetricCardsForStatistic,
    relevantGroupsForMonitoringMetricCard,
} from '@/components/monitoring/MonitoringMetricCard';
import StabilityIssuesContainer from '@/components/monitoring/StabilityIssuesContainer';
import { CollapsibleSection } from '@/components/monitoring/CollapsibleSection';
import RatingsReviews from '@/components/RatingsReviews';

const METRIC_CARDS_GRID_STYLE: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(232px, 1fr))',
    gap: 12,
};

interface RolloutGroupSummaryHeaderProps {
    orgId: string;
    appId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunIds: string[];
}

const seriesKeyForStatistic = (
    stat: Pick<MonitoringStatistic, 'type' | 'integrationInstallationId'> & Partial<MonitoringStatistic>,
) => JSON.stringify([stat.type, stat.integrationInstallationId, stat.eventName, stat.paramName]);

const unmergedStabilityTypes: Record<string, StabilityMetricType> = {
    [StabilityMetricType.MergedSessionStability]: StabilityMetricType.SessionStability,
    [StabilityMetricType.MergedUserStability]: StabilityMetricType.UserStability,
};

const unmergedSeriesKey = (seriesKey: string): string => {
    const [type, ...rest] = JSON.parse(seriesKey);
    return JSON.stringify([unmergedStabilityTypes[type] ?? type, ...rest]);
};

const isUserRatingsCard = (groups: { type: string }[]) =>
    groups.length > 0 &&
    groups.every((group) => AllUserRatingsMetricType.includes(group.type as UserRatingsMetricType));

const displayValuePartsForStatistic = (stat: MonitoringStatistic): { value: string; delta?: string } => {
    const value = displayMonitoringStatisticFormattedValue(stat);
    const delta = displayMonitoringStatisticFormattedDelta(stat);
    if (!delta || stat.currentValue === undefined || !stat.previousValue) {
        return { value };
    }
    return { value, delta: `${stat.currentValue >= stat.previousValue ? '↑' : '↓'} ${delta}` };
};

const DESTINATION_VALUES_POPOVER_PADDING = 12;

const CardWidthPopover: React.FC<{ content: React.ReactNode; children: React.ReactNode }> = ({ content, children }) => {
    const cardRef = React.useRef<HTMLDivElement>(null);
    const [cardWidth, setCardWidth] = React.useState<number>();

    return (
        <Popover
            overlayStyle={{ width: cardWidth && cardWidth + DESTINATION_VALUES_POPOVER_PADDING * 2 }}
            overlayInnerStyle={{ padding: DESTINATION_VALUES_POPOVER_PADDING }}
            onOpenChange={(open) => open && setCardWidth(cardRef.current?.offsetWidth)}
            content={content}
        >
            <div ref={cardRef}>{children}</div>
        </Popover>
    );
};

const destinationStatisticsForGroups = (
    groups: MonitoringStatisticGroup[],
    aggregatedStatistics: AggregatedMonitoringStatistic[],
    destinationsByStepConfigId: Record<string, Destination>,
): DestinationMonitoringStatistic[] => {
    const destinationStepConfigIds = Object.keys(destinationsByStepConfigId);
    const statistics = flatMap(groups, (group) => {
        const aggregated = aggregatedStatistics.find((stat) =>
            group.monitoringMetricId ?
                stat.monitoringMetricId === group.monitoringMetricId
            :   seriesKeyForStatistic(stat) === unmergedSeriesKey(group.seriesKey),
        );
        return aggregated?.destinationStatistics ?? [];
    }).filter((stat) => !!destinationsByStepConfigId[stat.destinationStepConfigId]);
    return sortBy(statistics, (stat) => destinationStepConfigIds.indexOf(stat.destinationStepConfigId));
};

const mergedStatusForGroups = (groups: MonitoringStatisticGroup[]): MonitoringMetricHealthStatus | undefined => {
    const statuses = compact(groups.map((group) => group.status));
    if (statuses.length === 0) {
        return undefined;
    }
    return statuses.includes(MonitoringMetricHealthStatus.Unhealthy) ?
            MonitoringMetricHealthStatus.Unhealthy
        :   MonitoringMetricHealthStatus.Healthy;
};

const isAndroidVitalsGroup = (group: MonitoringStatisticGroup) =>
    group.integrationType === IntegrationType.AndroidVitals;

const isWorstValueMergeableCard = (groups: MonitoringStatisticGroup[]) =>
    groups.length > 0 &&
    groups.every(
        (group) =>
            isAndroidVitalsGroup(group) ||
            (group.type !== StabilityMetricType.AdoptionRate &&
                AllStabilityMetricTypes.includes(group.type as StabilityMetricType)),
    );

const worstGroup = (groups: MonitoringStatisticGroup[]): MonitoringStatisticGroup => {
    const groupsWithValue = groups.filter((group) => !isNil(group.currentValue));
    if (groupsWithValue.length === 0) {
        return groups[0];
    }
    const isMoreBetter = groups[0].isMoreBetter ?? !isAndroidVitalsGroup(groups[0]);
    const pickWorst = isMoreBetter ? minBy : maxBy;
    return pickWorst(groupsWithValue, (group) => group.currentValue) ?? groupsWithValue[0];
};

interface AggregatedCard {
    card: GroupedMonitoringStatistic;
    sourceGroups: MonitoringStatisticGroup[];
}

const mergeCardsAcrossInstallationsByWorstValue = (cards: AggregatedCard[]): AggregatedCard[] => {
    const mergeKeyForCard = ({ card }: AggregatedCard) =>
        JSON.stringify([card.title, card.customDisplayName, uniq(card.groups.map((group) => group.type)).sort()]);
    const mergeableCardsByMergeKey = groupBy(
        cards.filter(({ card }) => isWorstValueMergeableCard(card.groups)),
        mergeKeyForCard,
    );
    return flatMap(cards, (aggregatedCard) => {
        if (!isWorstValueMergeableCard(aggregatedCard.card.groups)) {
            return [aggregatedCard];
        }
        const cardsToMerge = mergeableCardsByMergeKey[mergeKeyForCard(aggregatedCard)];
        if (cardsToMerge.length === 1) {
            return [aggregatedCard];
        }
        if (cardsToMerge[0] !== aggregatedCard) {
            return [];
        }
        const sourceGroups = flatMap(cardsToMerge, ({ sourceGroups }) => sourceGroups);
        const groupsByType = groupBy(sourceGroups, (group) => group.type);
        const groups = uniq(aggregatedCard.card.groups.map((group) => group.type)).map((type) =>
            worstGroup(groupsByType[type]),
        );
        return [
            {
                card: {
                    ...aggregatedCard.card,
                    key: `${aggregatedCard.card.key}-merged`,
                    groups,
                    mergedStatus: mergedStatusForGroups(sourceGroups),
                },
                sourceGroups,
            },
        ];
    });
};

const DestinationValuesPopoverContent: React.FC<{
    card: GroupedMonitoringStatistic;
    destinationStatistics: DestinationMonitoringStatistic[];
    destinationsByStepConfigId: Record<string, Destination>;
}> = ({ card, destinationStatistics, destinationsByStepConfigId }) => {
    const label = card.customDisplayName || card.title;

    return (
        <Flex vertical gap={8}>
            {destinationStatistics.map((stat) => {
                const destination = destinationsByStepConfigId[stat.destinationStepConfigId];
                if (!destination) {
                    return null;
                }
                const { value, delta } = displayValuePartsForStatistic(stat as MonitoringStatistic);
                return (
                    <Flex
                        key={`${stat.destinationStepConfigId}-${stat.type}`}
                        align="center"
                        gap={8}
                        style={{
                            position: 'relative',
                            height: 40,
                            padding: '0 12px 0 18px',
                            backgroundColor: antGrey.grey2,
                            border: `1px solid ${antGrey.grey4}`,
                            borderRadius: 6,
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: 5,
                                backgroundColor: colorForMonitoringMetricHealthStatus(stat.monitoringMetricStatus),
                            }}
                        />
                        <DestinationCounter destinations={[destination]} size={16} />
                        <IntegrationIcon integrationId={stat.integrationId} size={16} />
                        <Text
                            type="secondary"
                            ellipsis={{ tooltip: label }}
                            style={{ flex: 1, minWidth: 0, fontSize: 12 }}
                        >
                            {label}
                        </Text>
                        <Text style={{ flexShrink: 0, fontSize: 13 }}>{value}</Text>
                        {delta && <Text style={{ flexShrink: 0, fontSize: 13 }}>{delta}</Text>}
                    </Flex>
                );
            })}
        </Flex>
    );
};

const phasedReleaseStatusLabel = (releases: DestinationPhasedRelease[]): string => {
    const isFinished = (release: DestinationPhasedRelease) => release.isCompleted || release.isSuperseded;
    if (releases.some((release) => !isFinished(release) && !release.isPaused)) {
        return 'Active';
    }
    if (releases.some((release) => !isFinished(release))) {
        return 'Paused';
    }
    return releases.every((release) => release.isSuperseded) ? 'Superseded' : 'Completed';
};

const SummaryRowsCard: React.FC<{
    title: string;
    tag?: React.ReactNode;
    renderIcons: ((size: number) => React.ReactNode)[];
    footer?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, tag, renderIcons, footer, children }) => (
    <div
        style={{
            padding: '16px 20px 12px 22px',
            backgroundColor: runwayColors.white,
            border: `1px solid ${antGrey.grey5}`,
            borderRadius: 8,
            flex: 1,
        }}
    >
        <Flex vertical gap={10}>
            <Flex align="center" justify="space-between" style={{ height: 24 }}>
                <Flex align="center" gap={12}>
                    <Text
                        style={{
                            fontSize: 14,
                            lineHeight: '22px',
                            fontWeight: 600,
                            letterSpacing: 0,
                            color: antGrey.grey8,
                        }}
                    >
                        {title}
                    </Text>
                    {tag}
                </Flex>
                {renderIcons.length === 1 && renderIcons[0](16)}
                {renderIcons.length > 1 && (
                    <Popover
                        overlayInnerStyle={{ padding: '11px 12px' }}
                        content={
                            <Flex align="center" gap={24}>
                                {renderIcons.map((renderIcon, i) => (
                                    <React.Fragment key={i}>{renderIcon(16)}</React.Fragment>
                                ))}
                            </Flex>
                        }
                    >
                        <AppstoreOutlined style={{ color: antGrey.grey7, fontSize: 16 }} />
                    </Popover>
                )}
            </Flex>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '24px minmax(0, 1fr) 62px',
                    alignItems: 'center',
                    columnGap: 8,
                    rowGap: 4,
                }}
            >
                {children}
            </div>
            {footer}
        </Flex>
    </div>
);

const ROW_VALUE_TEXT_STYLE: React.CSSProperties = {
    fontSize: 12,
    lineHeight: '20px',
    fontWeight: 400,
    letterSpacing: '0.1em',
    color: antGrey.grey7,
};

const DestinationValueRow: React.FC<{
    destination: Destination;
    bar: React.ReactNode;
    trailingIcon?: React.ReactNode;
    value: string;
}> = ({ destination, bar, trailingIcon, value }) => (
    <>
        <Flex align="center" style={{ height: 22 }}>
            <DestinationCounter destinations={[destination]} size={20} />
        </Flex>
        <div>{bar}</div>
        <Flex align="center" justify="flex-end" gap={8}>
            {trailingIcon}
            <Text style={{ ...ROW_VALUE_TEXT_STYLE, whiteSpace: 'nowrap' }}>{value}</Text>
        </Flex>
    </>
);

const PhasedReleaseDestinationsCard: React.FC<{
    rows: { destination: Destination; release?: DestinationPhasedRelease; isReleased: boolean }[];
}> = ({ rows }) => {
    const storeIcons = compact(
        uniq(rows.map(({ destination }) => getIntegrationTypeForAppStoreType(destination.appStoreType))),
    ).map((integrationType) => iconAndAltForIntegrationType(integrationType));

    return (
        <SummaryRowsCard
            title="Phased release"
            tag={
                <Tag color={runwayColors.primary} style={{ ...tagStyle, margin: 0 }}>
                    {phasedReleaseStatusLabel(compact(rows.map(({ release }) => release))).toUpperCase()}
                </Tag>
            }
            renderIcons={storeIcons.map(([icon, alt]) => (size: number) => (
                <img src={icon} alt={alt} width={size} height={size} />
            ))}
        >
            {rows.map(({ destination, release, isReleased }) => {
                const percentage =
                    release ? release.phasedReleasePercent
                    : isReleased ? 100
                    : 0;
                return (
                    <DestinationValueRow
                        key={destination.stepConfigId}
                        destination={destination}
                        bar={
                            <PhasedReleaseChartBase
                                percentage={percentage}
                                dayNumber={release?.dayNumber ?? 0}
                                daysToPercent={release?.daysToPercentSchedule}
                                isAutomatedPhasedRollout={!!release?.isAutomatedPhasedRollout}
                                wasHalted={!!release?.isPaused}
                                wasAccelerated={!!release?.isAccelerated}
                                segmentBorderRadius={2}
                            />
                        }
                        trailingIcon={
                            release?.isAutomatedPhasedRollout && (
                                <ThunderboltFilled style={{ color: runwayColors.primary }} />
                            )
                        }
                        value={`${percentage}%`}
                    />
                );
            })}
        </SummaryRowsCard>
    );
};

const VersionAdoptionBar: React.FC<{ destination: Destination; versions: VersionAdoption[] }> = ({
    destination,
    versions,
}) => {
    const destinationName = displayStringForDestination(
        destination.platform,
        destination.appStoreType,
        destination.name,
        destination.appName,
    );
    const unknownAdoption = Math.max(100 - versions.reduce((sum, version) => sum + version.adoptionRate, 0), 0);

    return (
        <Flex align="center" gap={4} style={{ height: 8 }}>
            {versions.map((version) => (
                <Tooltip
                    key={version.version}
                    title={`${destinationName}: ${version.version} — ${Number(version.adoptionRate.toFixed(1))}%`}
                >
                    <div
                        style={{
                            flex: version.adoptionRate,
                            minWidth: 4,
                            height: 8,
                            borderRadius: 2,
                            backgroundColor: version.isCurrent ? runwayColors.primary : antGrey.grey5,
                        }}
                    />
                </Tooltip>
            ))}
            {unknownAdoption > 0 && (
                <div style={{ flex: unknownAdoption, height: 8, borderRadius: 2, backgroundColor: antGrey.grey4 }} />
            )}
        </Flex>
    );
};

const AdoptionRateDestinationsCard: React.FC<{
    integrationTypes: IntegrationType[];
    destinationAdoptions: { destination: Destination; adoption: DestinationAdoption }[];
}> = ({ integrationTypes, destinationAdoptions }) => (
    <SummaryRowsCard
        title="Adoption rate"
        renderIcons={integrationTypes.map((integrationType) => (size: number) => (
            <IntegrationIcon integrationId={integrationType} size={size} />
        ))}
    >
        {destinationAdoptions.length > 0 ?
            destinationAdoptions.map(({ destination, adoption }) => (
                <DestinationValueRow
                    key={destination.stepConfigId}
                    destination={destination}
                    bar={<VersionAdoptionBar destination={destination} versions={adoption.versions} />}
                    value={`${Number(adoption.adoptionRate.toFixed(1))}%`}
                />
            ))
        :   <Text style={{ ...ROW_VALUE_TEXT_STYLE, letterSpacing: 0, color: antGrey.grey6, gridColumn: '1 / -1' }}>
                No adoption rates known.
            </Text>
        }
    </SummaryRowsCard>
);

export const RolloutGroupSummaryHeader: React.FC<RolloutGroupSummaryHeaderProps> = ({
    orgId,
    appId,
    workflowId,
    workflowRunId,
    stepRunIds,
}) => {
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const demoStepRuns = useSelector((state: RootState) =>
        isViewingAppsInDemoMode ? state.ui.uiConfig?.demoData?.stepRunsDemoData : undefined,
    );
    const demoSummary = React.useMemo(
        () =>
            demoStepRuns ?
                buildDemoRolloutGroupSummary(compact(stepRunIds.map((stepRunId) => demoStepRuns[stepRunId])))
            :   undefined,
        [demoStepRuns, stepRunIds],
    );
    const summaryStepRuns = useSelector(
        (state: RootState) => selectStepRunsFromSummary(state, workflowRunId, stepRunIds),
        shallowEqual,
    );
    const rolloutStepRuns =
        demoStepRuns ? compact(stepRunIds.map((stepRunId) => demoStepRuns[stepRunId])) : summaryStepRuns;
    const { data: fetchedSummary, isLoading } = useGetRolloutGroupSummaryQuery(
        { orgId, workflowId, workflowRunId, stepRunIds },
        { skip: stepRunIds.length === 0 || isViewingAppsInDemoMode },
    );
    const summary = demoSummary ?? fetchedSummary;

    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, appId));
    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));
    const rolloutStepConfigs = useSelector((state: RootState) =>
        selectStepConfigsByType(state, appId, ReleaseStepType.Rollout),
    );
    const rolloutInstallationIds = React.useMemo(
        () =>
            uniq(
                flatMap(Object.values(rolloutStepConfigs), (stepConfig) => stepConfig.integrationInstallationIds ?? []),
            ),
        [rolloutStepConfigs],
    );

    const destinations = summary?.destinations ?? [];
    const destinationsByStepConfigId = React.useMemo(() => keyBy(destinations, 'stepConfigId'), [destinations]);

    const aggregatedCards = React.useMemo((): AggregatedCard[] => {
        if (!summary) {
            return [];
        }
        const grouped = massageMonitoringData({
            monitoringStatistics: summary.aggregatedMonitoringStatistics as MonitoringStatistic[],
            monitoringAutomations: summary.monitoringAutomations ?? [],
            metricsById,
            hasPartialData: true,
            stepInstallationIds: rolloutInstallationIds,
        });
        const cards = flatMap(flatMap(grouped, monitoringMetricCardsForStatistic), (card): AggregatedCard[] => {
            const displayedGroups = relevantGroupsForMonitoringMetricCard(card.groups);
            const groupsWithValue = displayedGroups.filter((group) => !isNil(group.currentValue));
            const groups = groupsWithValue.length > 0 ? groupsWithValue : displayedGroups.slice(0, 1);
            const groupsByInstallationId = groupBy(groups, (group) => JSON.parse(group.seriesKey)[1]);
            if (isUserRatingsCard(groups) && groupsWithValue.length > 1) {
                const averageRating = mean(groupsWithValue.map((group) => group.currentValue));
                const [firstGroup] = groupsWithValue;
                return [
                    {
                        card: {
                            ...card,
                            groups: [
                                {
                                    ...firstGroup,
                                    monitoringMetricId: undefined,
                                    currentValue: averageRating,
                                    value: displayMonitoringStatisticFormattedValue({
                                        integrationId: firstGroup.integrationType,
                                        type: firstGroup.type,
                                        currentValue: averageRating,
                                    } as MonitoringStatistic),
                                    delta: undefined,
                                    change: undefined,
                                    previousVersion: undefined,
                                    previousValue: undefined,
                                },
                            ],
                        },
                        sourceGroups: groupsWithValue,
                    },
                ];
            }
            if (Object.keys(groupsByInstallationId).length === 1) {
                return [{ card: { ...card, groups }, sourceGroups: groups }];
            }
            return Object.entries(groupsByInstallationId).map(([installationId, installationGroups]) => ({
                card: {
                    ...card,
                    key: `${card.key}-${installationId}`,
                    groups: installationGroups,
                    mergedStatus: mergedStatusForGroups(installationGroups),
                },
                sourceGroups: installationGroups,
            }));
        });
        return mergeCardsAcrossInstallationsByWorstValue(cards);
    }, [summary, metricsById, rolloutInstallationIds]);

    const unhealthyCards = React.useMemo(() => {
        if (!summary) {
            return [];
        }
        return flatMap(summary.unhealthyMonitoringStatistics, (stat) => {
            const grouped = massageMonitoringData({
                monitoringStatistics: [stat as MonitoringStatistic],
                monitoringAutomations: summary.monitoringAutomations ?? [],
                metricsById,
                hasPartialData: true,
                stepInstallationIds: rolloutInstallationIds,
                shouldGroupCrashFreeStats: false,
            });
            return flatMap(grouped, monitoringMetricCardsForStatistic)
                .filter((card) => card.groups.some((group) => group.status === MonitoringMetricHealthStatus.Unhealthy))
                .map((card) => ({ card, destinationStepConfigId: stat.destinationStepConfigId }));
        });
    }, [summary, metricsById, rolloutInstallationIds]);

    if (isLoading) {
        return (
            <Flex justify="center" style={{ padding: 24 }}>
                <Spin />
            </Flex>
        );
    }

    if (!summary || destinations.length === 0) {
        return null;
    }

    const adoptionEntries = flatMap(
        Object.entries(summary.adoptionsByInstallationId ?? {}),
        ([installationId, adoptions]) =>
            adoptions
                .filter((adoption) => !!destinationsByStepConfigId[adoption.destinationStepConfigId])
                .map((adoption) => ({
                    integrationType:
                        stabilityIntegrations.find((integration) => integration.installationId === installationId)
                            ?.id ?? (installationId.split(':')[0] as IntegrationType),
                    destination: destinationsByStepConfigId[adoption.destinationStepConfigId],
                    adoption,
                })),
    );
    const destinationAdoptions = sortBy(adoptionEntries, ({ destination }) => destinations.indexOf(destination));
    const adoptionIntegrationTypes =
        adoptionEntries.length > 0 ?
            uniq(adoptionEntries.map(({ integrationType }) => integrationType))
        :   compact([
                stabilityIntegrations.find((integration) => rolloutInstallationIds.includes(integration.installationId))
                    ?.id,
            ]);

    const phasedReleasesByDestinationStepId = summary.phasedReleasesByDestinationStepId ?? {};
    const releasedDestinationStepConfigIds = new Set(
        flatMap(rolloutStepRuns, (stepRun) =>
            (stepRun.data?.summaryData as RolloutStepRunData | undefined)?.releasedAt ?
                stepRun.destinations.map((destination) => destination.stepConfigId)
            :   [],
        ),
    );
    const phasedReleaseRows = destinations.map((destination) => ({
        destination,
        release: phasedReleasesByDestinationStepId[destination.stepConfigId],
        isReleased: releasedDestinationStepConfigIds.has(destination.stepConfigId),
    }));
    const hasPhasedRelease = phasedReleaseRows.some(({ release }) => !!release);
    const stabilityInfos = Object.entries(
        groupBy(
            Object.values(summary.stabilityInfosByInstallationId ?? {}),
            (stabilityInfo) => stabilityInfo.integrationId,
        ),
    ).map(([integrationId, infos]): [string, StabilityInfo] => {
        const mergedIssues = (issuesForInfo: (info: StabilityInfo) => StabilityIssue[] | undefined) =>
            sortBy(
                flatMap(infos, (info) => issuesForInfo(info) ?? []),
                (issue) => -issue.occurrencesCountInRelease,
            );
        return [
            integrationId,
            {
                ...infos[0],
                topStabilityIssues: mergedIssues((info) => info.topStabilityIssues),
                topNewStabilityIssues: mergedIssues((info) => info.topNewStabilityIssues),
                topStabilityIssuesNonFatal: mergedIssues((info) => info.topStabilityIssuesNonFatal),
                topNewStabilityIssuesNonFatal: mergedIssues((info) => info.topNewStabilityIssuesNonFatal),
            },
        ];
    });
    const haveReviews = summary.reviewData.recentReviews.length > 0 || summary.reviewData.issues.length > 0;

    return (
        <Flex vertical gap={12} style={{ padding: '0 36px 24px' }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: hasPhasedRelease ? '1fr 1fr' : '1fr',
                    gap: 12,
                }}
            >
                <AdoptionRateDestinationsCard
                    integrationTypes={adoptionIntegrationTypes}
                    destinationAdoptions={destinationAdoptions}
                />
                {hasPhasedRelease && <PhasedReleaseDestinationsCard rows={phasedReleaseRows} />}
            </div>

            {(aggregatedCards.length > 0 || unhealthyCards.length > 0) && (
                <div style={METRIC_CARDS_GRID_STYLE}>
                    {aggregatedCards.map(({ card, sourceGroups }) => {
                        const cardDestinationStatistics = destinationStatisticsForGroups(
                            sourceGroups,
                            summary.aggregatedMonitoringStatistics,
                            destinationsByStepConfigId,
                        );
                        const cardDestinations = uniq(
                            cardDestinationStatistics.map(
                                (stat) => destinationsByStepConfigId[stat.destinationStepConfigId],
                            ),
                        );
                        const metricCard = (
                            <MonitoringMetricCard
                                {...card}
                                leadingIcon={
                                    <DestinationCounter
                                        destinations={cardDestinations.length > 0 ? cardDestinations : destinations}
                                        size={18}
                                    />
                                }
                            />
                        );
                        const destinationStatistics = cardDestinationStatistics.filter(
                            (stat) => stat.currentValue !== undefined && stat.currentValue !== null,
                        );
                        if (destinationStatistics.length === 0) {
                            return <div key={`aggregated-${card.key}`}>{metricCard}</div>;
                        }
                        return (
                            <CardWidthPopover
                                key={`aggregated-${card.key}`}
                                content={
                                    <DestinationValuesPopoverContent
                                        card={card}
                                        destinationStatistics={destinationStatistics}
                                        destinationsByStepConfigId={destinationsByStepConfigId}
                                    />
                                }
                            >
                                {metricCard}
                            </CardWidthPopover>
                        );
                    })}
                    {unhealthyCards.map(({ card, destinationStepConfigId }) => {
                        const destination = destinationsByStepConfigId[destinationStepConfigId];
                        return (
                            <MonitoringMetricCard
                                key={`unhealthy-${destinationStepConfigId}-${card.key}`}
                                {...card}
                                leadingIcon={
                                    destination && <DestinationCounter destinations={[destination]} size={18} />
                                }
                            />
                        );
                    })}
                </div>
            )}

            {stabilityInfos.map(([integrationId, stabilityInfo]) => (
                <StabilityIssuesContainer
                    key={integrationId}
                    appId={appId}
                    stabilityInfo={stabilityInfo}
                    shouldHaveWhiteBackground
                    destinationsByStepConfigId={destinationsByStepConfigId}
                />
            ))}

            {haveReviews && (
                <CollapsibleSection title="Reviews" shouldHaveWhiteBackground>
                    <RatingsReviews
                        reviewData={summary.reviewData}
                        orgId={orgId}
                        appId={appId}
                        destinationsByStepConfigId={destinationsByStepConfigId}
                    />
                </CollapsibleSection>
            )}
        </Flex>
    );
};
